import React from "react";
import { useForm, Controller } from "react-hook-form";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import ExpandMoreOutlined from "@mui/icons-material/ExpandMoreOutlined";
import HelpOutlineOutlined from "@mui/icons-material/HelpOutlineOutlined";
import { StyledSelect } from "../../components/Select";
import { FormTextArea } from "../../components/Input";
import { notify } from "../../components/Notification";
import { useSendContactMessageMutation } from "../../services";
import Button, { ButtonTypes } from "../../components/Button";
import { FAQ_DATA, SUBJECT_OPTIONS } from "./constants";
import { ContactFormData } from "./types";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  ContactContainer,
  ContactForm,
  ContactField,
  ContactActions,
  Divider,
  FAQSection,
  FAQItem,
  FAQQuestion,
  FAQAnswer,
  FAQAnswerContent,
  FieldError,
  IconContainer,
  TitleRow,
  Label,
  FormGroup,
} from "./styles";

export const Contact: React.FC = () => {
  const [openFAQs, setOpenFAQs] = React.useState<Set<string>>(new Set());
  const [sendContactMessage] = useSendContactMessageMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    defaultValues: {
      subject: "general-question",
      message: "",
    },
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  // Toggle FAQ item
  const toggleFAQ = React.useCallback((id: string) => {
    setOpenFAQs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  // Handle form submission
  const onSubmit = React.useCallback(
    async (data: ContactFormData) => {
      try {
        const message = await sendContactMessage({
          subject: data.subject,
          message: data.message,
        }).unwrap();

        notify.success(message);

        reset();
      } catch {
        // errorToastMiddleware raises the toast; keep the message in the form.
      }
    },
    [sendContactMessage, reset],
  );

  const submitLabel = isSubmitting ? "Sending..." : "Send message";

  return (
    <ContactContainer>
      <Card className="contact">
        {/* Contact Form */}
        <CardHeader>
          <TitleRow>
            <IconContainer>
              <EmailOutlined />
            </IconContainer>
            <CardTitle>Contact us</CardTitle>
          </TitleRow>
          <CardDescription>
            Have a question or need help? Send a message and we'll get back to
            you as soon as possible.
          </CardDescription>
        </CardHeader>

        <ContactForm onSubmit={handleSubmit(onSubmit)}>
          {/* Subject Field */}
          <FormGroup>
            <Label>
              Subject <span style={{ color: "var(--color-error)" }}>*</span>
            </Label>
            <Controller
              name="subject"
              control={control}
              rules={{ required: "Please select a subject" }}
              render={({ field }) => (
                <StyledSelect
                  options={SUBJECT_OPTIONS}
                  ariaLabel="Subject"
                  value={SUBJECT_OPTIONS.find(
                    (opt) => opt.value === field.value,
                  )}
                  handleChange={(newValue) => {
                    const selectedOption = newValue as {
                      value?: string;
                    } | null;
                    if (selectedOption?.value) {
                      field.onChange(selectedOption.value);
                    }
                  }}
                />
              )}
            />
            {errors.subject && (
              <FieldError>{errors.subject.message}</FieldError>
            )}
          </FormGroup>

          {/* Message Field */}
          <ContactField>
            <FormGroup>
              <Label htmlFor="contact-message">
                Message <span style={{ color: "var(--color-error)" }}>*</span>
              </Label>
            </FormGroup>
            <FormTextArea
              name="message"
              control={control}
              rules={{
                required: "Message is required",
                minLength: {
                  value: 10,
                  message: "Message must be at least 10 characters",
                },
                maxLength: {
                  value: 2000,
                  message: "Message cannot exceed 2000 characters",
                },
              }}
              placeholder="Describe your issue or question..."
              disabled={isSubmitting}
              minHeight={150}
              maxLength={2000}
              showCharacterCount={true}
            />
          </ContactField>

          {/* Submit Button */}
          <ContactActions>
            <Button
              buttonType="submit"
              type={ButtonTypes.primary}
              disabled={isSubmitting}
            >
              <EmailOutlined />
              {submitLabel}
            </Button>
          </ContactActions>
        </ContactForm>

        <Divider />

        {/* FAQ Section */}
        <CardHeader>
          <TitleRow>
            <IconContainer>
              <HelpOutlineOutlined />
            </IconContainer>
            <CardTitle>Frequently asked questions</CardTitle>
          </TitleRow>
          <CardDescription>Quick answers to common questions.</CardDescription>
        </CardHeader>

        <FAQSection>
          {FAQ_DATA.map((faq) => (
            <FAQItem key={faq.id}>
              <FAQQuestion
                type="button"
                onClick={() => toggleFAQ(faq.id)}
                $isOpen={openFAQs.has(faq.id)}
                aria-expanded={openFAQs.has(faq.id)}
                aria-controls={`faq-answer-${faq.id}`}
              >
                <span>{faq.question}</span>
                <ExpandMoreOutlined />
              </FAQQuestion>
              <FAQAnswer
                $isOpen={openFAQs.has(faq.id)}
                id={`faq-answer-${faq.id}`}
              >
                <FAQAnswerContent>{faq.answer}</FAQAnswerContent>
              </FAQAnswer>
            </FAQItem>
          ))}
        </FAQSection>
      </Card>
    </ContactContainer>
  );
};
